import { asCseId, asRouteId, asRouteSlotId, asStopId, asWorkOrderId } from 'types/entities/shared/ids'

import { deriveRecurrence } from './deriveRecurrence'

import type { DailyStopApi } from 'types/api/route/dailyStop'
import type { Address, GeoPoint } from 'types/entities/Address'
import type { WeekDay } from 'types/entities/shared'
import type { DailyStop, OnRequestStop } from 'types/modules/routing'

const ADAPTER_NAME = 'fromDailyStopApi'

const ONE_OFF_EVENT_TYPE = 'one off'

/**
 * Daily stop endpoint scopes by routeId in the URL path; serviceDate is on the
 * row payload. dayOfWeek must be derived by the caller (we don't import date-fns
 * here to keep the adapter pure and dependency-light).
 */
export interface DailyStopAdapterContext {
  dayOfWeek: WeekDay | null
}

const buildAddress = (dto: DailyStopApi): Address | null => {
  if (!dto.line1) return null
  return {
    line1: dto.line1,
    unitNumber: dto.unitNumber,
    city: dto.city,
    state: dto.state,
    zipCode: dto.zipCode,
    country: dto.country,
  }
}

const buildGeo = (lat: number | null, lng: number | null): GeoPoint | null =>
  lat !== null && lng !== null ? { latitude: lat, longitude: lng } : null

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0

const warnBadField = (field: string, value: unknown, dtoId: unknown): void => {
  // eslint-disable-next-line no-console
  console.warn(`[${ADAPTER_NAME}] dropping row — bad ${field}`, { value, dtoId })
}

/**
 * On-request detection — the type-system safety net that keeps on-request stops
 * out of the master cascade. Mirrors `getIsTemporaryEvent` in
 * `master-route/board-view/utils.js`. Any of the three signals is sufficient.
 */
const isOnRequest = (dto: DailyStopApi): boolean => {
  if (dto.pricedServiceType === 'On Request') return true
  if (dto.inMaster === false) return true
  if (typeof dto.configuredServiceEventType === 'string' && dto.configuredServiceEventType.toLowerCase() === ONE_OFF_EVENT_TYPE) {
    return true
  }
  return false
}

/**
 * Adapt a raw {@link DailyStopApi} row to either a {@link DailyStop} or
 * {@link OnRequestStop}. The discriminator (`kind`) and the literal `null`
 * `configuredServiceEventId` make on-request stops *structurally* unable to
 * flow into a master pipeline — the master step types reject `null`.
 *
 * Compromise on errors: returns `null` (and `console.warn`s) when a required
 * field is missing/malformed instead of throwing — adapter `README.md`'s
 * "don't halt user flow" rule + the routing utility's "surface bad data" stance.
 */
export const fromDailyStopApi = (dto: DailyStopApi, context: DailyStopAdapterContext): DailyStop | OnRequestStop | null => {
  if (!isNonEmptyString(dto.workOrderId)) {
    warnBadField('workOrderId', dto.workOrderId, dto.workOrderId)
    return null
  }
  if (!isNonEmptyString(dto.routeId)) {
    warnBadField('routeId', dto.routeId, dto.workOrderId)
    return null
  }
  if (!isNonEmptyString(dto.serviceDate)) {
    warnBadField('serviceDate', dto.serviceDate, dto.workOrderId)
    return null
  }
  if (typeof dto.sequence !== 'number' || !Number.isFinite(dto.sequence)) {
    warnBadField('sequence', dto.sequence, dto.workOrderId)
    return null
  }

  const onRequest = isOnRequest(dto)

  // Non-on-request rows need a CSE id; on-request rows correctly carry null.
  if (!onRequest && !isNonEmptyString(dto.configuredServiceEventId)) {
    warnBadField('configuredServiceEventId', dto.configuredServiceEventId, dto.workOrderId)
    return null
  }

  const recurrence = deriveRecurrence({
    daysOfWeek: dto.daysOfWeek,
    recurrenceFrequency: dto.recurrenceFrequency,
    recurrenceInterval: dto.recurrenceInterval,
    recurrencePer: dto.recurrencePer,
    pricedServiceType: dto.pricedServiceType,
    configuredServiceEventType: dto.configuredServiceEventType,
  })

  const base = {
    workOrderId: asWorkOrderId(dto.workOrderId),
    stopId: asStopId(dto.workOrderId),
    routeSlotId: dto.routeSlotId != null ? asRouteSlotId(dto.routeSlotId) : null,
    workOrderNumber: dto.workOrderNumber,
    workOrderStatus: dto.workOrderStatus,
    servicedAt: dto.servicedAt,
    configuredServiceId: dto.configuredServiceId,
    accountId: dto.accountId,
    accountName: dto.accountName,
    addressName: dto.addressName,
    locationName: dto.locationName,
    address: buildAddress(dto),
    geo: buildGeo(dto.latitude, dto.longitude),
    serviceName: dto.serviceName,
    serviceActionType: dto.serviceActionType,
    eventActionType: dto.eventActionType,
    pricedServiceType: dto.pricedServiceType,
    recurrence,
    tags: dto.tags,
    exceptions: dto.exceptions ?? [],
    inMaster: dto.inMaster,
    suspensionHold: dto.suspensionHold,
    endDate: dto.endDate,
    serviceEnded: dto.serviceEnded,
    serviceDeactivated: dto.serviceDeactivated,
    serviceStatus: dto.serviceStatus,
    eventDeleted: dto.eventDeleted,
    routeAssignment: {
      routeId: asRouteId(dto.routeId),
      sequence: dto.sequence,
      dayOfWeek: context.dayOfWeek,
      serviceDate: dto.serviceDate,
    },
  }

  if (onRequest) {
    return {
      ...base,
      kind: 'on-request',
      configuredServiceEventId: null,
    }
  }

  return {
    ...base,
    kind: 'daily',
    configuredServiceEventId: asCseId(dto.configuredServiceEventId),
  }
}

/** List adapter — drops malformed rows (each emits its own `console.warn`). */
export const fromDailyStopListApi = (
  dtos: ReadonlyArray<DailyStopApi>,
  context: DailyStopAdapterContext
): ReadonlyArray<DailyStop | OnRequestStop> =>
  dtos.map(dto => fromDailyStopApi(dto, context)).filter((s): s is DailyStop | OnRequestStop => s !== null)
